import type { PlayerCharacter } from "../types/entity.js";
import { DEATH_SAVE_NONE } from "../types/entity.js";
import type { CampEligibilityResult } from "./campEligibility.js";

/**
 * Short- and long-rest resolvers.
 *
 * Both functions are pure — they take the current member array and an
 * options object, and return a summary plus a new member array. Callers
 * are responsible for writing the new members back via
 * `PartyStore.setMembers(...)` (or equivalent) and for pushing log /
 * feed messages from the summary.
 *
 * Per the party-and-characters spec (§Rest, §Resolved):
 *  - **Short rest**: partial HP recovery (flat fraction of maxHp — no
 *    hit dice pool), refund one lowest-tier spell slot to every caster.
 *    Does NOT clear death-save state. Consumes rations.
 *  - **Long rest (camp)**: full HP, all slots refunded to the max
 *    passed in, death saves cleared (dead revived), consumes rations.
 *
 * Maximum spell slots aren't tracked on `PlayerCharacter` in v1 — the
 * caller can pass a per-character slot-cap map (derived from class +
 * level). When no cap is supplied, the short-rest refund is uncapped
 * and long-rest leaves slots unchanged. This keeps rest.ts independent
 * of the class-data registry.
 */

// ── Short rest ───────────────────────────────────────────────────────────────

export interface ShortRestOptions {
  /** Fraction of maxHp restored to each not-dead character. Default 0.25. */
  readonly hpRecoveryFraction?: number;
  /**
   * Optional per-character cap for the level-1 slot refund. Keys are
   * character instanceIds; values are the character's full slot table.
   * A missing entry means no cap on that character.
   */
  readonly maxSpellSlots?: ReadonlyMap<string, Readonly<Record<number, number>>>;
  /** Total rations in the party purse. Each participating character eats 1. */
  readonly rationsAvailable?: number;
}

export interface ShortRestSummary {
  readonly success: boolean;
  readonly reason?: "insufficient_rations";
  readonly rationsConsumed: number;
  /** HP points added to each character's current HP. */
  readonly hpHealed: ReadonlyMap<string, number>;
  /** Number of level-1 slots refunded per character. */
  readonly slotsRefunded: ReadonlyMap<string, number>;
  /** New member list (identity-equal to the input on failure). */
  readonly nextMembers: readonly PlayerCharacter[];
}

const DEFAULT_HP_FRACTION = 0.25;

/** A character is "dead" when death saves hit 3 failures. */
function isDeadCharacter(m: PlayerCharacter): boolean {
  const death = (m as Partial<{ deathSaves: { failures: number } }>).deathSaves;
  return death !== undefined && death.failures >= 3;
}

/** A character is a "caster" if their spellSlots object has any numeric keys. */
function isCaster(m: PlayerCharacter): boolean {
  return Object.keys(m.spellSlots).length > 0;
}

export function resolveShortRest(
  members: readonly PlayerCharacter[],
  options: ShortRestOptions = {},
): ShortRestSummary {
  const fraction = options.hpRecoveryFraction ?? DEFAULT_HP_FRACTION;
  const rationsNeeded = members.length;
  if (options.rationsAvailable !== undefined && options.rationsAvailable < rationsNeeded) {
    return {
      success: false,
      reason: "insufficient_rations",
      rationsConsumed: 0,
      hpHealed: new Map(),
      slotsRefunded: new Map(),
      nextMembers: members,
    };
  }

  const hpHealed = new Map<string, number>();
  const slotsRefunded = new Map<string, number>();
  const nextMembers: PlayerCharacter[] = [];

  for (const m of members) {
    let next = m;

    // HP recovery — only for non-dead characters.
    if (!isDeadCharacter(m)) {
      const heal = Math.floor(m.maxHp * fraction);
      const newHp = Math.min(m.maxHp, m.hp + heal);
      const gained = newHp - m.hp;
      if (gained > 0) {
        next = { ...next, hp: newHp };
        hpHealed.set(m.instanceId, gained);
      }
    }

    // Refund one level-1 slot per caster, capped by max if provided.
    if (isCaster(m)) {
      const cap = options.maxSpellSlots?.get(m.instanceId)?.[1];
      const current = m.spellSlots[1] ?? 0;
      const refunded = cap !== undefined ? Math.min(cap, current + 1) - current : 1;
      if (refunded > 0) {
        next = {
          ...next,
          spellSlots: { ...next.spellSlots, 1: current + refunded },
        };
        slotsRefunded.set(m.instanceId, refunded);
      }
    }

    nextMembers.push(next);
  }

  return {
    success: true,
    rationsConsumed: rationsNeeded,
    hpHealed,
    slotsRefunded,
    nextMembers,
  };
}

// ── Long rest ────────────────────────────────────────────────────────────────

export interface LongRestOptions {
  /**
   * Per-character max-slot table. When present the character's
   * spellSlots are fully restored to these values; when absent the
   * character's slots are left untouched.
   */
  readonly maxSpellSlots?: ReadonlyMap<string, Readonly<Record<number, number>>>;
  readonly rationsAvailable?: number;
  /** Rations per character; default 1. */
  readonly rationsPerCharacter?: number;
  /**
   * Result of a `checkCampEligibility` call the caller already made (C08b).
   * When provided and `!ok`, the long rest is refused without mutation and
   * the refusal reason is mirrored in the summary. When absent, the check
   * is skipped — preserves the "pure resolver" callers that don't know
   * about world state.
   */
  readonly eligibility?: CampEligibilityResult;
}

export type LongRestRefusalReason = "insufficient_rations" | "hostiles_nearby";

export interface LongRestSummary {
  readonly success: boolean;
  readonly reason?: LongRestRefusalReason;
  readonly rationsConsumed: number;
  /** instanceIds of characters who were previously dead (failures ≥ 3) and are now revived at full HP. */
  readonly revivedIds: readonly string[];
  readonly nextMembers: readonly PlayerCharacter[];
}

export function resolveLongRest(
  members: readonly PlayerCharacter[],
  options: LongRestOptions = {},
): LongRestSummary {
  // C08b: camp-eligibility gate — refuse before spending rations.
  if (options.eligibility && !options.eligibility.ok) {
    return {
      success: false,
      reason: options.eligibility.reason ?? "hostiles_nearby",
      rationsConsumed: 0,
      revivedIds: [],
      nextMembers: members,
    };
  }

  const perChar = options.rationsPerCharacter ?? 1;
  const rationsNeeded = members.length * perChar;
  if (options.rationsAvailable !== undefined && options.rationsAvailable < rationsNeeded) {
    return {
      success: false,
      reason: "insufficient_rations",
      rationsConsumed: 0,
      revivedIds: [],
      nextMembers: members,
    };
  }

  const revivedIds: string[] = [];
  const nextMembers: PlayerCharacter[] = [];

  for (const m of members) {
    const wasDead = isDeadCharacter(m);
    let next: PlayerCharacter = { ...m, hp: m.maxHp };

    // Clear death-save state on Character (failures, successes, stable) if present.
    if ("deathSaves" in next) {
      (next as PlayerCharacter & { deathSaves: typeof DEATH_SAVE_NONE }).deathSaves = DEATH_SAVE_NONE;
    }

    const cap = options.maxSpellSlots?.get(m.instanceId);
    if (cap) {
      next = { ...next, spellSlots: { ...cap } };
    }

    if (wasDead) revivedIds.push(m.instanceId);
    nextMembers.push(next);
  }

  return {
    success: true,
    rationsConsumed: rationsNeeded,
    revivedIds,
    nextMembers,
  };
}
